import { useRef } from 'react';
import { useDrag, useDrop } from 'react-dnd';

import { useForceUpdate } from '@work4all/utils/lib/hooks/use-force-update';

// ToDo: WW-7459 Move to shared shadow folder
import { TABLE_ROW_DND_ITEM_TYPE } from '../../../../../../../../erp/components/tab-panels/positions/components/edit-table/components/TableRow/constants';
import { DropZoneType, EditTableEntry } from '../../types';

interface UseEditTableDragProps {
  canDrop: (input: { id: string; index: number }) => number;
  index: number;
  localId: string;
  moveRowDone: (
    localId: string,
    index: number,
    dropZoneType: DropZoneType
  ) => void;
  getHovered?: () => EditTableEntry;
  getCurrent?: (id: string) => EditTableEntry;
  isMoveAllowed?: (from: EditTableEntry, to: EditTableEntry) => boolean;
  onBeforeDrag?: () => void;
}

type ITableRowDndItem = {
  id: string;
  index: number;
};

let dndItemRef = undefined;
let dropZoneTypeRef: DropZoneType = 'before';

export const useEditTableDrag = <
  TDrop = HTMLDivElement,
  TDrag = HTMLDivElement
>(
  props: UseEditTableDragProps
) => {
  const { canDrop, index, localId, moveRowDone, getHovered, onBeforeDrag } =
    props;

  const dropRef = useRef<TDrop>(null);
  const dragRef = useRef<TDrag>(null);

  const forceUpdate = useForceUpdate();

  const [{ isOver, direction }, drop] = useDrop({
    accept: TABLE_ROW_DND_ITEM_TYPE,
    canDrop: (item, monitor) => {
      const canDropValue = canDrop(monitor.getItem());
      return Boolean(canDropValue);
    },
    collect: (monitor) => {
      const canDropValue = canDrop(monitor.getItem());

      return {
        isOver: monitor.isOver() && canDropValue,
        direction: canDropValue > 0 ? 'up' : 'down',
      };
    },
    hover(item: ITableRowDndItem, monitor) {
      if (!dropRef.current) {
        return;
      }

      // Calculate dropZoneType based on mouse position
      const canDropValue = canDrop(monitor.getItem());
      let dropZoneType: DropZoneType = 'before';

      if (monitor.isOver() && dropRef.current) {
        const clientOffset = monitor.getClientOffset();
        const elementRect = (dropRef.current as any).getBoundingClientRect();

        if (clientOffset && elementRect) {
          const relativeY = clientOffset.y - elementRect.top;
          const elementHeight = elementRect.height;

          if (relativeY < elementHeight * 0.5) {
            dropZoneType = 'before';
          } else {
            dropZoneType = 'after';
          }
        }
      } else {
        // For non-headers, use the existing logic
        dropZoneType = canDropValue > 0 ? 'before' : 'after';
      }

      // Update the ref to trigger collect re-run
      if (dropZoneTypeRef !== dropZoneType && getHovered) {
        const hovered = getHovered();

        if (hovered) {
          dropZoneTypeRef = dropZoneType;
          forceUpdate();
        }
      }

      const dragIndex = item.index;
      const hoverIndex = index;
      // Don't replace items with themselves
      if (dragIndex === hoverIndex) {
        return;
      }
      dndItemRef = { id: item.id, index: hoverIndex };
    },
  });

  const [{ isDragging }, drag, preview] = useDrag<
    ITableRowDndItem,
    unknown,
    { isDragging: boolean }
  >({
    type: TABLE_ROW_DND_ITEM_TYPE,
    item: () => {
      onBeforeDrag?.();
      return { id: localId, index };
    },
    options: {
      dropEffect: 'move',
    },
    collect(monitor) {
      return {
        isDragging: monitor.isDragging(),
      };
    },
    end() {
      if (dndItemRef) {
        moveRowDone?.(dndItemRef.id, dndItemRef.index, dropZoneTypeRef);
        dropZoneTypeRef = 'before';
      }
    },
  });

  preview(drop(dropRef));
  drag(dragRef);

  return {
    isOver,
    direction,
    isDragging,
    dropRef,
    dragRef,
    dropZone: dropZoneTypeRef,
  };
};
